import { useEffect, useState, useCallback } from "react";
import { Pencil, Trash2, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi } from "../../lib/api";
import { inputCls, labelCls, AiAssistButton } from "./shared";

const EMPTY_AIRLINE = {
  slug: "", name: "", code: "", tagline: "", hero_headline: "", hero_subtext: "", description: "",
  highlights: [], default_dep: "KHI", default_ret: "DXB", top_destinations: [], faqs: [],
  seo_title: "", seo_description: "", published: true, sort_order: 99,
};
const EMPTY_ROUTE = {
  slug: "", origin_city: "", origin_code: "", dest_city: "", dest_code: "", price_from: 0, duration: "",
  airlines: [], description: "", faqs: [], seo_title: "", seo_description: "", published: true, sort_order: 99,
};

function EditorDialog({ open, onClose, doc, kind, onSaved, isNew }) {
  const [form, setForm] = useState(doc);
  const [faqsText, setFaqsText] = useState("");
  const [extraText, setExtraText] = useState("");
  const [offerRulesText, setOfferRulesText] = useState("{}");

  useEffect(() => {
    setForm(doc);
    setFaqsText(JSON.stringify(doc?.faqs || [], null, 2));
    setOfferRulesText(JSON.stringify(doc?.offer_rules || {}, null, 2));
    if (kind === "airline") setExtraText(JSON.stringify(doc?.top_destinations || [], null, 2));
    else setExtraText((doc?.airlines || []).join(", "));
  }, [doc, kind]);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    try {
      const payload = { ...form, sort_order: +form.sort_order || 0, faqs: JSON.parse(faqsText) };
      if (kind === "airline") {
        payload.top_destinations = JSON.parse(extraText);
        payload.offer_rules = JSON.parse(offerRulesText);
        payload.highlights = typeof form.highlights === "string" ? form.highlights.split("\n").filter(Boolean) : form.highlights;
      } else {
        payload.airlines = extraText.split(",").map((s) => s.trim()).filter(Boolean);
        payload.price_from = +form.price_from || 0;
      }
      const client = adminApi();
      const base = kind === "airline" ? "/admin/airlines" : "/admin/routes";
      if (isNew) await client.post(base, payload);
      else await client.put(`${base}/${doc.slug}`, payload);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid JSON in FAQs/destinations or save failed");
    }
  };

  const scalarFields = kind === "airline"
    ? ["slug", "name", "code", "tagline", "brand_color", "hero_headline", "hero_subtext", "default_dep", "default_ret", "seo_title", "seo_description", "sort_order"]
    : ["slug", "origin_city", "origin_code", "dest_city", "dest_code", "price_from", "duration", "seo_title", "seo_description", "sort_order"];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isNew ? "Create" : "Edit"} {kind}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {scalarFields.map((f) => (
            <div key={f} className={["hero_subtext", "seo_title", "seo_description"].includes(f) ? "col-span-2" : ""}>
              <label className={labelCls}>{f.replace(/_/g, " ")}</label>
              <input data-testid={`editor-${f}-input`} className={inputCls} value={form[f] ?? ""} onChange={set(f)} disabled={f === "slug" && !isNew} />
            </div>
          ))}
          <div className="col-span-2">
            <label className={labelCls}>description</label>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-slate-400">Markdown supported on articles</span>
              <AiAssistButton kind="description" subject={`${kind === "airline" ? "the airline" : "the route"} ${form.slug} (${form.name || `${form.origin_city} to ${form.dest_city}`})`} provider="claude" context={form.description} onGenerated={(t) => setForm({ ...form, description: t })} label="Description" />
            </div>
            <textarea className={`${inputCls} h-20`} value={form.description ?? ""} onChange={set("description")} data-testid="editor-description-input" />
          </div>
          {kind === "airline" && (
            <div className="col-span-2">
              <label className={labelCls}>highlights (one per line)</label>
              <textarea className={`${inputCls} h-20`} value={Array.isArray(form.highlights) ? form.highlights.join("\n") : form.highlights} onChange={set("highlights")} data-testid="editor-highlights-input" />
            </div>
          )}
          <div className="col-span-2">
            <label className={labelCls}>{kind === "airline" ? "top destinations (JSON)" : "airlines (comma separated)"}</label>
            <textarea className={`${inputCls} h-24 font-mono text-xs`} value={extraText} onChange={(e) => setExtraText(e.target.value)} data-testid="editor-extra-input" />
          </div>
          {kind === "airline" && (
            <div className="col-span-2">
              <label className={labelCls}>offer rules override (JSON, e.g. {"{"}&quot;days_ahead&quot;:2,&quot;stay_days&quot;:7,&quot;trip_type&quot;:&quot;oneway&quot;,&quot;cabin&quot;:&quot;Y&quot;{"}"})</label>
              <textarea className={`${inputCls} h-16 font-mono text-xs`} value={offerRulesText} onChange={(e) => setOfferRulesText(e.target.value)} data-testid="editor-offer-rules-input" />
            </div>
          )}
          <div className="col-span-2">
            <label className={labelCls}>FAQs (JSON: [{"{"}&quot;q&quot;,&quot;a&quot;{"}"}])</label>
            <textarea className={`${inputCls} h-24 font-mono text-xs`} value={faqsText} onChange={(e) => setFaqsText(e.target.value)} data-testid="editor-faqs-input" />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" checked={!!form.published} onChange={set("published")} className="accent-[#970C10] w-4 h-4" data-testid="editor-published-checkbox" />
            Published
          </label>
        </div>
        <button onClick={save} data-testid="editor-save-btn" className="w-full bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold py-2.5 rounded-lg transition-colors duration-200">
          Save
        </button>
      </DialogContent>
    </Dialog>
  );
}

export function ContentTab({ kind }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const base = kind === "airline" ? "/admin/airlines" : "/admin/routes";

  const load = useCallback(() => {
    adminApi().get(base).then((r) => setItems(r.data)).catch(() => {});
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const remove = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    await adminApi().delete(`${base}/${slug}`);
    toast.success("Deleted");
    load();
  };

  const verifyPrice = async (slug) => {
    await adminApi().post(`/admin/routes/${slug}/verify`);
    toast.success("Marked as verified");
    load();
  };

  const daysSince = (iso) => {
    if (!iso) return Infinity;
    const diff = Date.now() - new Date(iso).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          data-testid={`admin-add-${kind}-btn`}
          onClick={() => { setEditing(kind === "airline" ? EMPTY_AIRLINE : EMPTY_ROUTE); setIsNew(true); }}
          className="flex items-center gap-2 bg-[#970C10] hover:bg-[#7A0A0D] text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors duration-200"
        >
          <Plus className="w-4 h-4" /> New {kind}
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">{kind === "airline" ? "Name" : "Route"}</th>
              {kind === "route" && <th className="px-4 py-3">Price verified</th>}
              <th className="px-4 py-3">Published</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const days = kind === "route" ? daysSince(it.last_verified_at) : 0;
              const stale = kind === "route" && days > 30;
              return (
                <tr key={it.slug} className="border-t border-slate-100" data-testid={`admin-row-${it.slug}`}>
                  <td className="px-4 py-3 font-mono text-xs">{it.slug}</td>
                  <td className="px-4 py-3 font-semibold">{kind === "airline" ? it.name : `${it.origin_city} → ${it.dest_city}`}</td>
                  {kind === "route" && (
                    <td className="px-4 py-3 text-xs">
                      {it.last_verified_at ? (
                        <span className={stale ? "text-amber-700 font-bold" : "text-slate-500"} data-testid={`route-verified-${it.slug}`}>
                          {days === 0 ? "Today" : days === Infinity ? "Never" : `${days}d ago`}{stale && " · stale"}
                        </span>
                      ) : (
                        <span className="text-amber-700 font-bold" data-testid={`route-verified-${it.slug}`}>Never</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${it.published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {it.published ? "Live" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {kind === "route" && (
                      <button
                        data-testid={`admin-verify-${it.slug}`}
                        onClick={() => verifyPrice(it.slug)}
                        title="Confirm price is still current"
                        className="text-slate-500 hover:text-green-700 transition-colors duration-200"
                      >
                        <CheckCircle2 className="w-4 h-4 inline" />
                      </button>
                    )}
                    <button data-testid={`admin-edit-${it.slug}`} onClick={() => { setEditing(it); setIsNew(false); }} className="text-slate-500 hover:text-[#970C10] transition-colors duration-200">
                      <Pencil className="w-4 h-4 inline" />
                    </button>
                    <button data-testid={`admin-delete-${it.slug}`} onClick={() => remove(it.slug)} className="text-slate-500 hover:text-red-600 transition-colors duration-200">
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <EditorDialog open={!!editing} onClose={() => setEditing(null)} doc={editing} kind={kind} onSaved={load} isNew={isNew} />
    </div>
  );
}
